// Sign-in modal, /login, /signup, /forgotpassword, /resetpassword
// (components/auth/JamAuth). See messages/README.md for how to add strings.
//
// auth.error.<scope>.<code> are the Firebase error texts from utils/tools.js
// (custom*ErrorMessages), one scope per map because the English differs
// between them. authErrorText() in JamAuth.js picks them by error.code.
export const auth = {
  en: {
    // Shared
    "auth.continueGoogle": "Continue with Google",
    "auth.orEmail": "or with email",
    "auth.email": "Email",
    "auth.emailInput": "Email Input",
    "auth.emailPlaceholder": "you@example.com",
    "auth.password": "Password",
    "auth.passwordInput": "Password Input",
    "auth.passwordPlaceholderNew": "6+ characters",
    "auth.signUp": "Sign up",
    "auth.logIn": "Log in",
    "auth.signIn": "Sign in",
    "auth.createAccount": "Create account",
    "auth.createYourAccount": "Create your account",
    "auth.forgotPassword": "Forgot password?",
    "auth.agree": "I agree to the",
    "auth.agreeJammable": "I agree to Jammable's",
    "auth.and": "and",
    "auth.terms": "Terms of Use",
    "auth.privacy": "Privacy Policy",
    "auth.agreeRequired":
      "Please agree to our Terms of Use and Privacy Policy.",
    "auth.welcomeBack": "Welcome back!",
    "auth.welcomeNew": "Welcome to Jammable!",
    "auth.registerError":
      "An error occurred during registration. Please try again.",
    "auth.backToLogin": "Back to log in",

    // Form validation
    "auth.invalidEmail": "Invalid email",
    "auth.emailRequired": "Email is required",
    "auth.passwordMin": "Password must be at least 6 characters",
    "auth.passwordRequired": "Password is required",

    // SignInModal
    "auth.modal.signInTitle": "Sign in to Jammable",
    "auth.modal.subtitle": "Make AI covers with 60,000+ voices.",
    "auth.modal.signUpButton": "Sign Up Button",
    "auth.modal.signInButton": "Sign In Button",
    "auth.modal.haveAccount": "Have an account?",
    "auth.modal.newHere": "New here?",
    "auth.modal.createAnAccount": "Create an account",

    // /login
    "auth.login.heading": "Welcome back",
    "auth.login.blurb":
      "Your covers, voices and collections are where you left them.",
    "auth.login.noAccount": "Don't have an account?",

    // /signup
    "auth.signup.heading": "Make any voice sing",
    "auth.signup.headingHighlight": "your audio",
    "auth.signup.blurb":
      "60,000+ voices, your covers saved in one place, and a studio for duets and videos.",
    "auth.signup.haveAccount": "Already have an account?",

    // /forgotpassword
    "auth.forgot.title": "Reset your password",
    "auth.forgot.blurb":
      "Enter your email and we'll send you a link to set a new one.",
    "auth.forgot.send": "Send reset link",
    "auth.forgot.sentToast": "Password reset email sent!",
    "auth.forgot.sent": "Sent. Check your inbox and spam folder.",
    "auth.forgot.google": "Signed up with Google?",
    "auth.forgot.googleAfter": "instead",

    // /resetpassword
    "auth.reset.title": "Set a new password",
    "auth.reset.blurb": "At least 6 characters. You'll log in with it next.",
    "auth.reset.newPassword": "New password",
    "auth.reset.submit": "Reset password",
    "auth.reset.success": "Password reset successful!",
    "auth.reset.expired": "Link expired?",
    "auth.reset.sendNew": "Send a new one",

    // Firebase errors: Google popup
    "auth.error.google.popup-closed-by-user":
      "The popup window was closed before completing the sign-in process. Please try again.",
    "auth.error.google.popup-blocked":
      "The popup window was blocked by the browser. Please enable popups and try again.",
    "auth.error.google.operation-not-supported-in-this-environment":
      "This sign-in method is not supported in your current environment.",
    "auth.error.google.network-request-failed":
      "A network error occurred. Please check your internet connection and try again.",
    "auth.error.google.cancelled-popup-request":
      "Multiple sign-in requests were detected. Please try again.",
    "auth.error.google.timeout":
      "The sign-in process took too long. Please check your internet connection and try again.",
    "auth.error.google.account-exists-with-different-credential":
      "An account with this email address already exists. Please sign in with the correct provider.",
    "auth.error.google.invalid-credential":
      "The provided sign-in credential is invalid or expired. Please try again.",
    "auth.error.google.user-disabled":
      "Your account has been disabled. Please contact support for assistance.",
    "auth.error.google.user-not-found":
      "No account found with this email address. Please try again or sign up for a new account.",
    "auth.error.google.wrong-password":
      "The password provided is incorrect. Please try again.",

    // Firebase errors: email sign-in
    "auth.error.signIn.invalid-email":
      "The email address you entered is invalid. Please check and try again.",
    "auth.error.signIn.user-disabled":
      "Your account has been disabled. Please contact support for assistance.",
    "auth.error.signIn.user-not-found":
      "No account found with this email address. Please try again or sign up for a new account.",
    "auth.error.signIn.wrong-password":
      "The password you entered is incorrect. Please try again.",
    "auth.error.signIn.too-many-requests":
      "Too many unsuccessful login attempts. Please try again later.",

    // Firebase errors: email sign-up
    "auth.error.signUp.email-already-in-use":
      "This email address is already in use by another account. Please use a different email.",
    "auth.error.signUp.invalid-email":
      "The email address you entered is invalid. Please check and try again.",
    "auth.error.signUp.operation-not-allowed":
      "Creating new accounts is currently not allowed. Please try again later.",
    "auth.error.signUp.weak-password":
      "The password must be at least 6 characters long. Please try a stronger password.",

    // Firebase errors: sending the reset email
    "auth.error.forgot.invalid-email": "Please enter a valid email address.",
    "auth.error.forgot.user-not-found": "No account found with this email.",
    "auth.error.forgot.missing-android-pkg-name":
      "An Android package name is required.",
    "auth.error.forgot.missing-continue-uri": "A continue URL is required.",
    "auth.error.forgot.missing-ios-bundle-id": "An iOS Bundle ID is required.",
    "auth.error.forgot.unauthorized-continue-uri":
      "The provided continue URL is not allowed.",
    "auth.error.forgot.operation-not-allowed":
      "Password reset is not enabled for this project.",

    // Firebase errors: setting the new password
    "auth.error.reset.invalid-action-code":
      "The action code is invalid. Please check the link and try again.",
    "auth.error.reset.expired-action-code":
      "The action code has expired. Please request a new password reset email.",
    "auth.error.reset.user-disabled":
      "This user account has been disabled. Please contact support.",
    "auth.error.reset.user-not-found": "No account found with this email.",
    "auth.error.reset.weak-password":
      "The password must be at least 6 characters.",
    "auth.error.reset.operation-not-allowed":
      "Password reset is not enabled for this project.",
    "auth.seo.loginTitle": "Login - Jammable",
    "auth.seo.loginDescription": "Login to your Jammable account.",
    "auth.seo.signupTitle": "Sign Up | Jammable",
  },
};
